import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CompanyService } from 'src/app/modules/settings/services/company.service';
import { BranchService } from './branch.service';
import { DropdownInputsSignalService } from './dropdown-inputs-signal.service';

/** Absolute path used when neither branch nor company has a logo. */
export const DEFAULT_LOGO_URL = 'assets/media/logos/company-placeholder.png';

@Injectable({
  providedIn: 'root'
})
export class LogoStateService {

  private readonly _companyLogo$ = new BehaviorSubject<string | null>(null);
  private readonly _branchLogo$  = new BehaviorSubject<string | null>(null);

  /**
   * Resolved logo URL following the priority rule:
   *   Branch Logo → Company Logo → Default Image
   *
   * Components can use `async` pipe: `[src]="logoStateService.displayLogo$ | async"`.
   */
  readonly displayLogo$: Observable<string> = combineLatest([
    this._branchLogo$,
    this._companyLogo$,
  ]).pipe(
    map(([branch, company]) => branch || company || DEFAULT_LOGO_URL)
  );

  constructor(
    private companyService: CompanyService,
    private branchService: BranchService,
    private signalService: DropdownInputsSignalService,
  ) {
    // ── Wire signal bus → logo refresh (single place, no duplication in components) ──
    this.signalService.reload$.subscribe((signal) => {
      if (signal === 'updateCompanyLogo') {
        this.refreshCompanyLogo();
      }
      if (signal === 'updateBranchLogo' || signal === 'branchSelected') {
        this.refreshBranchLogo();
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  /** Re-fetch company logo from the API and push result into the stream. */
  refreshCompanyLogo(): void {
    this.companyService.getDefault()
      .subscribe({
        next: (res) => {
          const url = this.extractLogoUrl(res?.data);
          // Always update — clears the BehaviorSubject when url is falsy (logo deleted)
          this._companyLogo$.next(url ? this.addCacheBuster(url) : null);
        },
        error: () => { /* keep last known value on error */ }
      });
  }

  /** Re-fetch the currently selected branch logo from the API and push result. */
  refreshBranchLogo(): void {
    const branchId = this.getSelectedBranchId();
    const source$ = branchId
      ? this.branchService.getBranch(branchId)
      : this.branchService.getDefaultBranch();

    source$.subscribe({
      next: (res) => {
        const raw = res?.data ?? res;
        const url = this.extractLogoUrl(raw);
        // Always update — clears BehaviorSubject when url is falsy (logo removed)
        this._branchLogo$.next(url ? this.addCacheBuster(url) : null);
      },
      error: () => { /* keep last known value on error */ }
    });
  }

  /** Refresh both company and branch logos — call once on application and component init. */
  refreshAll(): void {
    this.refreshCompanyLogo();
    this.refreshBranchLogo();
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  /**
   * Extracts a logo URL from API data, checking all known field names in priority order:
   * `symbolImageUrl` (Company/default endpoint) → `logoUrl` → `logoAttachment.url` → `logoAttachment.fullPath`
   */
  extractLogoUrl(data: any): string {
    if (!data) return '';
    const url =
      data.symbolImageUrl ??
      data.logoUrl ??
      data.logoAttachment?.url ??
      data.logoAttachment?.fullPath ??
      '';
    return typeof url === 'string' ? url : '';
  }

  private addCacheBuster(url: string): string {
    if (!url) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}_t=${Date.now()}`;
  }

  /** Reads the current user's selected branch ID from localStorage. */
  private getSelectedBranchId(): number | null {
    try {
      const raw    = sessionStorage.getItem('userProfile');
      const userId = raw ? JSON.parse(raw)?.id : null;
      const key    = userId ? `selected_branch_id_user_${userId}` : 'selected_branch_id';
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      const parsed = Number(stored);
      return isNaN(parsed) ? null : parsed;
    } catch {
      return null;
    }
  }
}
